import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/format";
import { Trash2, ShieldCheck } from "lucide-react";
import { ConditionMeter } from "@/components/ConditionRating";
import { useBrandLogos } from "@/lib/brandLogos";

export default function Cart() {
  const { items, removeFromCart, subtotal } = useCart();
  const { logoFor } = useBrandLogos();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="container-lookers py-24 text-center" data-testid="cart-empty-state">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/lookers/branding/doodle_empty_bag.png`} alt="" className="w-28 h-28 object-contain mx-auto mb-4" />
        <h1 className="font-heading font-extrabold text-4xl mb-4 text-neutral-900">Your Bag Is Empty</h1>
        <p className="text-sm text-zinc-500 mb-6">Because every pair is unique, once it's gone, it's gone. Find your next look.</p>
        <Link to="/shop" className="bg-teal-600 hover:bg-charcoal text-white px-6 py-3 rounded-full text-xs font-heading font-bold uppercase tracking-wide transition-colors">Shop Frames</Link>
      </div>
    );
  }

  return (
    <div className="container-lookers py-12" data-testid="cart-page">
      <h1 className="font-heading font-extrabold text-4xl mb-8 text-neutral-900">Your Bag</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {items.map((i) => {
            const savingsPct = i.original_rrp ? Math.round(((i.original_rrp - i.price) / i.original_rrp) * 100) : null;
            const logoUrl = logoFor(i.brand_slug);
            return (
              <div key={i.product_id} className="flex gap-4 border-b border-neutral-100 pb-6" data-testid={`cart-item-${i.product_id}`}>
                <img src={i.image} alt={i.model} className="w-24 h-24 object-contain rounded-xl bg-[#F8F8F9] border border-neutral-200/70 p-2" />
                <div className="flex-1">
                  {logoUrl ? (
                    <img src={logoUrl} alt={i.brand_name} className="h-4 w-auto object-contain max-w-[100px] mb-1" />
                  ) : (
                    <div className="text-xs uppercase tracking-widest text-neutral-900 font-heading font-bold">{i.brand_name}</div>
                  )}
                  <div className="font-body text-neutral-600 text-sm mb-1.5">{i.model}</div>
                  <ConditionMeter score={i.condition_score} label={i.condition_label} />
                  {i.lens_upgrade && <div className="text-xs text-emerald-600 mt-1.5">+ Clear non-prescription lenses (+{money(i.lens_price)})</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-lg text-neutral-900">{money(i.price + (i.lens_price || 0))}</div>
                  {i.original_rrp && (
                    <div className="flex flex-col items-end gap-1 mt-0.5">
                      <span className="font-mono text-xs text-neutral-400 line-through">{money(i.original_rrp)}</span>
                      {savingsPct > 0 && <span className="text-[10px] font-mono font-bold uppercase text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">{savingsPct}% Off</span>}
                    </div>
                  )}
                  <button onClick={() => removeFromCart(i.product_id)} className="text-neutral-400 mt-2 hover:text-rose-600 transition-colors" aria-label="Remove" data-testid={`cart-remove-${i.product_id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border border-neutral-200 rounded-2xl bg-white p-6 h-fit">
          <div className="flex justify-between text-sm mb-2 text-neutral-800">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal" className="font-mono font-bold">{money(subtotal)}</span>
          </div>
          <p className="text-xs text-neutral-400 mb-6">Shipping and any store credit applied at checkout.</p>
          <button onClick={() => navigate("/checkout")} className="w-full bg-teal-600 hover:bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wide py-4 rounded-full transition-colors" data-testid="proceed-to-checkout-button">
            Proceed To Checkout
          </button>
          <p className="flex items-start gap-1.5 text-[11px] text-neutral-400 mt-4" data-testid="cart-authenticity-note">
            <ShieldCheck size={13} className="text-teal-600 shrink-0 mt-0.5" />
            <span>Genuine designer eyewear. Individually checked by Lookers. <Link to="/authenticity" className="text-teal-700 underline underline-offset-2">Authenticity Guarantee</Link></span>
          </p>
        </div>
      </div>
    </div>
  );
}
